import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import RyeLogo from 'components/Logo';
import { useAuth } from 'providers/AuthProvider';
import { defaultLoginRedirectFlow, useQuery } from 'routes';
import { ReactComponent as GoogleIcon } from 'assets/icons/social/google.svg';
import config, { Routes } from 'config/constants';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { Typography } from 'components/ui/typography';
import Separator from 'components/Separator';
import './style.scss';
import { SOURCE, ACTION } from 'app/analytics';
import { trackSegmentEvent } from 'app/utils';
import { GoogleSignUpFlow } from './GoogleSignUpFlow';
import { addProtocolIfNotPresent } from 'utils/addProtocolIfNotPresent';
import { env } from 'config/env';
import { shouldRequireEmailVerification } from 'app/emailVerification';
import { EmailVerificationGate } from 'components/EmailVerificationGate';
import { useFlags } from 'providers/FlagsProvider';

const DEFAULT_VALUE = '';

export enum RegisterFormState {
  INITIAL = 'initial',
  USER_CHOSE_GOOGLE = 'userChoseGoogle',
  SUBMITTING_EMAIL = 'submittingEmail',
}

const RegisterPage = () => {
  const refInputPassword = useRef<HTMLInputElement | null>(null);
  const { requireEmailVerification } = useFlags();
  const allQueryParams = useQuery();
  const queryRedirect = allQueryParams.get('redirect');
  const queryEmail = allQueryParams.get('email');

  // Form state
  const [formState, setFormState] = useState<RegisterFormState>(RegisterFormState.INITIAL);

  // Form fields
  const [firstName, setFirstName] = useState(DEFAULT_VALUE);
  const [lastName, setLastName] = useState(DEFAULT_VALUE);
  const [email, setEmail] = useState(queryEmail || DEFAULT_VALUE);
  const [website, setWebsite] = useState('');
  const [password, setPassword] = useState(DEFAULT_VALUE);
  const [, refreshEmailVerificationState] = useState(0);

  const { search } = useLocation();
  const loginLink = search ? `${Routes.Login}${search}` : Routes.Login;
  const navigate = useNavigate();

  const { user, error, loading, signInOrRegisterWithGoogle, registerWithEmailAndPassword } =
    useAuth();
  const requiresEmailVerification = shouldRequireEmailVerification(user, requireEmailVerification);

  // Auto-focus the password input on mount if we have an email in the URL
  useEffect(() => {
    if (queryEmail) {
      focusOnPassword();
    }
  }, [queryEmail]);

  // Display auth errors to the user and clear loading state
  useEffect(() => {
    if (error) {
      alert(error);
      setFormState((formState) =>
        // Don't revert to email registration if the user chose Google auth
        formState === RegisterFormState.SUBMITTING_EMAIL ? RegisterFormState.INITIAL : formState,
      );
    }
  }, [error]);

  const onRegistrationSuccess = () =>
    defaultLoginRedirectFlow(
      navigate,
      loading,
      user,
      queryRedirect,
      () => navigate(env.VITE_NODE_ENV !== 'staging' ? Routes.Payment : Routes.Dashboard),
      allQueryParams ? `?${allQueryParams}` : '',
    );

  const focusOnPassword = () => {
    if (refInputPassword && refInputPassword.current) {
      refInputPassword.current.focus();
    }
  };

  const handleOnSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    // Update UI to show that we're in the process of submitting
    setFormState(RegisterFormState.SUBMITTING_EMAIL);

    const registeredUser = await registerWithEmailAndPassword(
      firstName,
      lastName,
      email,
      website,
      password,
    );

    trackSegmentEvent(SOURCE.SIGNUP_FORM, ACTION.CLICK, 'register_with_email_button', {
      email,
      firstName,
      lastName,
    });

    if (registeredUser) {
      window.dataLayer.push({
        event: 'account_signup_success',
        environment: env.VITE_NODE_ENV,
      });

      if (!shouldRequireEmailVerification(registeredUser, requireEmailVerification)) {
        onRegistrationSuccess();
      }
    }
  };

  if (!loading && user && requiresEmailVerification) {
    return (
      <EmailVerificationGate
        user={user}
        autoSendOnMount
        onVerified={() => {
          refreshEmailVerificationState((value) => value + 1);
          onRegistrationSuccess();
        }}
      />
    );
  }

  if (loading || (user && queryRedirect)) {
    return null;
  }

  return (
    <div className="sign-up">
      {/* left section */}
      <div className="sign-up-container">
        <RyeLogo to={Routes.Login} />
        <Typography variant="body" as="p" className="sign-up-tagline">
          Sign up for a Rye account to start building the future of eCommerce
        </Typography>
        <Typography variant="small" as="div" className="sign-up-account-link">
          Already have an account?{' '}
          <Typography variant="link" asChild>
            <Link to={loginLink}>Sign in</Link>
          </Typography>
        </Typography>
      </div>

      {/* right section */}
      <div className="sign-up-container">
        <Typography variant="h2" as="h1" className="sign-up-header">
          Create your account
        </Typography>

        {formState === RegisterFormState.USER_CHOSE_GOOGLE ? (
          // If the user wants to sign up with Google, we need to collect additional info first
          <GoogleSignUpFlow
            setFormState={setFormState}
            signInOrRegisterWithGoogle={signInOrRegisterWithGoogle}
            onRegistrationSuccess={onRegistrationSuccess}
          />
        ) : (
          <>
            {/* Trigger the Google sign-up flow */}
            <Button
              variant="outline"
              className="sign-up-google"
              onClick={() => setFormState(RegisterFormState.USER_CHOSE_GOOGLE)}
            >
              <GoogleIcon /> Sign up with Google
            </Button>

            <Separator text="or" />

            {/* Email sign-up form */}
            <form className="sign-up-form" onSubmit={handleOnSubmit}>
              <Input
                className="sign-up-input"
                placeholder="First name"
                type="text"
                name="first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
              <Input
                className="sign-up-input"
                placeholder="Last name"
                type="text"
                name="last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
              <Input
                className="sign-up-input"
                placeholder="Work email address"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.trim())}
                required
              />
              <Input
                className="sign-up-input"
                placeholder="Website"
                type="url"
                name="website"
                value={website}
                onBlur={() => setWebsite(addProtocolIfNotPresent(website))}
                onChange={(e) => setWebsite(e.target.value.trim())}
                required
              />
              <Input
                ref={refInputPassword}
                className="sign-up-input"
                placeholder="Password"
                type="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value.trim())}
                required
              />

              <Button
                type="submit"
                variant="secondary"
                disabled={formState === RegisterFormState.SUBMITTING_EMAIL}
              >
                {formState === RegisterFormState.SUBMITTING_EMAIL
                  ? 'Signing up...'
                  : 'Sign up with email'}
              </Button>
            </form>
          </>
        )}

        <Typography variant="small" as="p" className="sign-up-terms">
          By clicking "Sign up with Google/email" above, you acknowledge that you have read and
          understood, and agree to Rye's{' '}
          <Typography variant="link" asChild>
            <a href={config.external.terms} target="_blank" rel="noopener noreferrer">
              Terms and Conditions
            </a>
          </Typography>{' '}
          and{' '}
          <Typography variant="link" asChild>
            <a href={config.external.privacyPolicy} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
          </Typography>
          .
        </Typography>
      </div>
    </div>
  );
};

export default RegisterPage;
